import {languageFromPath, localizePath} from '../i18n';
import routes from '../data/routes.json';
import {useEffect} from 'react';
import seo from '../data/seo.json';

/**
 * Titre, description et canonique **par route**.
 *
 * ## Ce que ce site perdait
 *
 * C'est une application cliente : un seul `index.html`, donc un seul titre. Les cinq routes
 * partageaient « Le Bouddoir - Restaurant Aix en Provence », la même description et la même
 * canonique. Pour un moteur, cinq pages qui se déclarent identiques sont quatre pages de trop —
 * et l'onglet du navigateur ne disait jamais où l'on était.
 *
 * Les valeurs viennent de `seo.json`, le même fichier que le générateur lit pour écrire le HTML
 * de la page d'accueil et le sitemap. Une seule source, trois consommateurs.
 *
 * ## Les routes inconnues sont retirées de l'index
 *
 * Une application cliente répond **200** sur n'importe quelle adresse : `/nimporte-quoi` rend la
 * page « introuvable » avec un code de succès. Sans `noindex`, un moteur indexe donc autant de
 * fausses pages qu'il existe de liens morts pointant vers ce site, toutes avec le titre de
 * l'accueil.
 *
 * ## Pourquoi c'est impératif et non déclaratif
 *
 * React 18 ne remonte pas les balises `<head>` rendues dans un composant — c'est une nouveauté
 * de React 19. Une bibliothèque dédiée ferait le travail, mais pour six balises elle pèserait
 * plus que le problème.
 */
type Meta = {
    readonly title: string;
    readonly description: string;
    /**
     * Retirée de l'index, sans cesser d'être accessible.
     *
     * Les pages légales **doivent** être atteignables — la LCEN l'exige — mais elles n'ont
     * aucune raison d'être indexées. Google les remontait en liens de site sous le résultat
     * principal, à côté des cartes et des photos : quatre liens là où trois suffisent, dont
     * deux qu'aucun client ne cherche.
     *
     * `noindex` ne bloque pas l'accès, il retire de l'index. Les deux ne sont pas la même
     * chose, et c'est ce qui rend ce choix légitime.
     */
    readonly noindex?: boolean;
};

const parChemin = new Map(seo.pages.map((page) => [page.path, page]));
const accueil = parChemin.get('/');

/** Trouve ou crée la balise, puis pose sa valeur. Idempotent : deux navigations n'en font pas deux. */
const poser = (selecteur: string, creer: () => HTMLElement, attribut: string, valeur: string): void => {
    const existante = document.head.querySelector<HTMLElement>(selecteur);
    const noeud = existante ?? document.head.appendChild(creer());
    noeud.setAttribute(attribut, valeur);
};

const meta = (cle: 'name' | 'property', nom: string, valeur: string): void =>
    poser(`meta[${cle}="${nom}"]`, () => {
        const noeud = document.createElement('meta');
        noeud.setAttribute(cle, nom);
        return noeud;
    }, 'content', valeur);

export const usePageMeta = (pathname: string): void => {
    useEffect(() => {
        const locale = languageFromPath(pathname);
        document.documentElement.lang = locale;
        const page: Meta | undefined = parChemin.get(pathname);
        const introuvable = page === undefined;
        const horsIndex = introuvable || page.noindex === true;
        const titre = introuvable ? `${locale === 'en' ? 'Page not found' : 'Page introuvable'} — ${seo.pages[0].title.split('—')[0].trim()}` : page.title;
        const description = introuvable ? (accueil?.description ?? '') : page.description;
        const canonique = new URL(introuvable ? (locale === 'en' ? '/en' : '/') : pathname, `${seo.origin}/`).href;

        document.title = titre;
        meta('property', 'og:locale', locale === 'en' ? 'en_GB' : 'fr_FR');
        document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(link => link.remove());
        if (Object.values(routes).some(route => Object.values(route).includes(pathname))) {
            for (const lang of ['fr', 'en', 'x-default'] as const) {
                const link = document.createElement('link');
                link.rel = 'alternate'; link.hreflang = lang;
                link.href = new URL(localizePath(pathname, lang === 'en' ? 'en' : 'fr'), seo.origin).href;
                document.head.append(link);
            }
        }
        meta('name', 'description', description);
        meta('property', 'og:title', titre);
        meta('property', 'og:description', description);
        meta('property', 'og:url', canonique);
        meta('name', 'twitter:title', titre);
        meta('name', 'twitter:description', description);

        // La canonique d'une route inconnue désigne l'accueil : elle ne doit pas se déclarer
        // canonique d'elle-même, sinon on demande l'indexation de ce qu'on vient d'exclure.
        poser('link[rel="canonical"]', () => {
            const noeud = document.createElement('link');
            noeud.setAttribute('rel', 'canonical');
            return noeud;
        }, 'href', canonique);

        // Retirée en repartant : un `noindex` laissé en place condamnerait la page suivante.
        const robots = document.head.querySelector('meta[name="robots"]');
        if (horsIndex) meta('name', 'robots', 'noindex, follow');
        else robots?.remove();
    }, [pathname]);
};
